import { useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import {
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import styles from '../styles/automationNew.module.scss';
import { RUN_LABEL } from '../constants';
import StatusPill, { dotClassForStatus } from './StatusPill';

function looksLikeUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

function displayUrl(meta = {}, active = null) {
  const candidates = [meta.pageUrl, active?.pageUrl, active?.description];
  return candidates.find((value) => looksLikeUrl(value)) || meta.pageUrl || active?.pageUrl || null;
}

function executorStatusKey({ live, runLabel }) {
  if (live || runLabel === RUN_LABEL.RUNNING) return 'running';
  if (runLabel === RUN_LABEL.CONNECTING) return 'connecting';
  if (runLabel === RUN_LABEL.ERROR) return 'error';
  if (runLabel === RUN_LABEL.COMPLETED) return 'completed';
  if (runLabel === RUN_LABEL.STOPPED) return 'stopped';
  return 'idle';
}

export default function LiveBrowserExecutor({
  isRunning = false,
  runLabel = RUN_LABEL.IDLE,
  screenshot = null,
  screenshots = [],
  meta = {},
}) {
  const live = isRunning || runLabel === RUN_LABEL.RUNNING;
  const statusKey = executorStatusKey({ live, runLabel });
  const [followLive, setFollowLive] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const previewCardRef = useRef(null);
  const closePreviewBtnRef = useRef(null);

  useEffect(() => {
    if (!live) return;
    if (followLive) {
      setSelectedIdx(-1);
    }
  }, [screenshots.length, live, followLive]);

  useEffect(() => {
    if (live) setFollowLive(true);
  }, [live]);

  const latestIdx = screenshots.length > 0 ? screenshots.length - 1 : -1;
  const activeIdx = followLive || selectedIdx < 0 ? latestIdx : selectedIdx;
  const active = useMemo(() => {
    if (activeIdx >= 0 && screenshots[activeIdx]) return screenshots[activeIdx];
    return screenshot || screenshots[screenshots.length - 1] || null;
  }, [activeIdx, screenshot, screenshots]);

  useEffect(() => {
    setZoom(1);
    setImgLoaded(false);
  }, [activeIdx, active?.url]);

  const pageUrl = displayUrl(meta, active);
  const urlLabel = pageUrl || (live ? 'Executing…' : 'Waiting for agent…');

  const openPreview = () => {
    if (!active?.url) return;
    setPreviewOpen(true);
  };

  const selectShot = (index) => {
    setFollowLive(false);
    setSelectedIdx(index);
  };

  const resumeLive = () => {
    setFollowLive(true);
    setSelectedIdx(-1);
  };

  const stepPreview = (delta) => {
    if (screenshots.length === 0) return;
    const base = activeIdx >= 0 ? activeIdx : latestIdx;
    const next = Math.max(0, Math.min(screenshots.length - 1, base + delta));
    setFollowLive(false);
    setSelectedIdx(next);
  };

  const zoomIn = () => setZoom((z) => Math.min(3, z + 0.5));
  const zoomOut = () => setZoom((z) => Math.max(1, z - 0.5));
  const resetZoom = () => setZoom(1);

  const handleDownload = () => {
    if (!active?.url) return;
    const link = document.createElement('a');
    link.href = active.url;
    link.download = `screenshot-${Math.max(activeIdx, 0) + 1}.png`;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  useEffect(() => {
    if (!previewOpen) return undefined;
    const previousFocus = document.activeElement;
    const focusTimer = window.setTimeout(() => {
      closePreviewBtnRef.current?.focus?.();
    }, 0);

    const onKey = (event) => {
      if (event.key === 'Tab') {
        const root = previewCardRef.current;
        if (!root) return;
        const focusable = root.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (event.key === 'Escape') setPreviewOpen(false);
      if (event.key === 'ArrowLeft') {
        setFollowLive(false);
        setSelectedIdx((prev) => {
          const base = prev >= 0 ? prev : screenshots.length - 1;
          return Math.max(0, base - 1);
        });
      }
      if (event.key === 'ArrowRight') {
        setFollowLive(false);
        setSelectedIdx((prev) => {
          const base = prev >= 0 ? prev : screenshots.length - 1;
          return Math.min(screenshots.length - 1, base + 1);
        });
      }
      if (event.key === '+' || event.key === '=') zoomIn();
      if (event.key === '-') zoomOut();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKey);
      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };
  }, [previewOpen, screenshots.length]);

  const browserMeta = [meta.browser, meta.viewport].filter(Boolean).join(' • ');
  const footerBrowser = [
    meta.browser ? `Browser: ${meta.browser}` : null,
    meta.viewport ? `Viewport: ${meta.viewport}` : null,
    `Shots: ${screenshots.length}`,
    activeIdx >= 0 ? `Viewing ${activeIdx + 1}/${screenshots.length}` : null,
  ]
    .filter(Boolean)
    .join(' | ');

  return (
    <section className={styles.panel} aria-label="Executor Live Browser">
      <div className={styles.panelHeader}>
        <div className={styles.executorHeaderMeta}>
          <StatusPill
            status={statusKey}
            label={live ? 'LIVE' : runLabel}
          />
          <span>
            Executing: Instruction {meta.instructionCurrent || 0} of {meta.instructionTotal || 0} • Action{' '}
            {meta.actionCurrent || 0} of {meta.actionTotal || 0}
          </span>
          {browserMeta && <span>{browserMeta}</span>}
        </div>
        <span className={styles.panelTitle}>Executor</span>
      </div>

      <div className={`${styles.panelBody} ${styles.executorBody}`}>
        <div className={styles.viewport}>
          <div className={styles.fakeBrowserChrome}>
            <div className={styles.fakeDots} aria-hidden>
              <span className={styles.fakeDot} />
              <span className={styles.fakeDot} />
              <span className={styles.fakeDot} />
            </div>
            <div
              className={styles.fakeUrl}
              title={pageUrl || undefined}
              role="status"
              aria-label="Current page URL"
            >
              <span className={styles.fakeUrlScheme} aria-hidden>
                {pageUrl?.startsWith('https') ? 'https' : pageUrl?.startsWith('http') ? 'http' : 'url'}
              </span>
              <span className={styles.fakeUrlText}>{urlLabel}</span>
            </div>
            {!followLive && screenshots.length > 0 && (
              <button type="button" className={styles.followLiveBtn} onClick={resumeLive}>
                Follow live
              </button>
            )}
          </div>

          {active?.url ? (
            <button
              type="button"
              className={styles.screenshotFrame}
              onClick={openPreview}
              title="Click to enlarge preview"
              aria-label="Open screenshot preview"
            >
              <img
                src={active.url}
                alt={active.description || pageUrl || 'Live screenshot'}
                className={styles.screenshotImg}
              />
              <span className={styles.previewHint}>Click to enlarge · images update live</span>
              {meta.targetLabel && (
                <div className={styles.targetTooltipFloating}>
                  <strong>TARGET: {meta.targetLabel}</strong>
                  {meta.confidence != null && <>Confidence: {meta.confidence}%</>}
                </div>
              )}
            </button>
          ) : (
            <div className={`${styles.executorEmpty} ${live ? styles.executorEmptyLive : ''}`}>
              <div className={styles.executorEmptyIcon} aria-hidden>
                <ComputerDesktopIcon className={styles.executorEmptyHeroIcon} />
              </div>
              <h3 className={styles.executorEmptyTitle}>
                {live ? 'Capturing live browser…' : 'Live browser preview'}
              </h3>
              {pageUrl ? (
                <p className={styles.executorEmptyUrl} title={pageUrl}>
                  {pageUrl}
                </p>
              ) : (
                <p className={styles.executorEmptyBody}>
                  {live
                    ? 'Connected to the agent. Screenshots will appear here as steps run.'
                    : 'Select an environment and press Run to see live execution.'}
                </p>
              )}
              {live && <span className={dotClassForStatus('running')} aria-hidden />}
            </div>
          )}

          {screenshots.length > 0 && (
            <div className={styles.shotStrip}>
              {screenshots.map((shot, index) => (
                <button
                  key={`${shot.url}-${index}`}
                  type="button"
                  className={`${styles.shotThumb} ${index === activeIdx ? styles.shotThumbActive : ''}`}
                  onClick={() => selectShot(index)}
                  title={shot.pageUrl || shot.description || `Screenshot ${index + 1}`}
                  aria-label={`Screenshot ${index + 1}${index === activeIdx ? ', selected' : ''}`}
                  aria-current={index === activeIdx ? 'true' : undefined}
                >
                  <img src={shot.url} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={`${styles.panelFooter} ${styles.executorFooter}`}>
        <StatusPill status={statusKey} label={live ? 'LIVE' : runLabel} />
        <span className={styles.footerUrl} title={pageUrl || undefined}>
          URL: {pageUrl || '—'}
        </span>
        <span>{footerBrowser || '—'}</span>
      </div>

      {previewOpen && active?.url && (
        <div
          className={styles.previewModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="automation-new-preview-title"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            ref={previewCardRef}
            className={styles.previewModalCard}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.previewModalHeader}>
              <div
                id="automation-new-preview-title"
                className={styles.previewModalUrl}
                title={pageUrl || undefined}
              >
                {pageUrl || active.description || 'Screenshot'}
              </div>
              <div className={styles.previewModalActions}>
                {!followLive && (
                  <button type="button" className={styles.followLiveBtn} onClick={resumeLive}>
                    Follow live
                  </button>
                )}
                <Tooltip title="Zoom out">
                  <span>
                    <button
                      type="button"
                      className={styles.previewIconBtn}
                      onClick={zoomOut}
                      disabled={zoom <= 1}
                      aria-label="Zoom out"
                    >
                      <MagnifyingGlassMinusIcon />
                    </button>
                  </span>
                </Tooltip>
                <button
                  type="button"
                  className={styles.previewZoomLabel}
                  onClick={resetZoom}
                  title="Reset zoom"
                  aria-label={`Zoom ${zoom.toFixed(1)}x, reset`}
                >
                  {zoom.toFixed(1)}x
                </button>
                <Tooltip title="Zoom in">
                  <span>
                    <button
                      type="button"
                      className={styles.previewIconBtn}
                      onClick={zoomIn}
                      disabled={zoom >= 3}
                      aria-label="Zoom in"
                    >
                      <MagnifyingGlassPlusIcon />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title="Previous">
                  <span>
                    <button
                      type="button"
                      className={styles.previewIconBtn}
                      onClick={() => stepPreview(-1)}
                      disabled={activeIdx <= 0}
                      aria-label="Previous screenshot"
                    >
                      <ChevronLeftIcon />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title="Next">
                  <span>
                    <button
                      type="button"
                      className={styles.previewIconBtn}
                      onClick={() => stepPreview(1)}
                      disabled={activeIdx < 0 || activeIdx >= screenshots.length - 1}
                      aria-label="Next screenshot"
                    >
                      <ChevronRightIcon />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title="Download">
                  <span>
                    <button
                      type="button"
                      className={styles.previewIconBtn}
                      onClick={handleDownload}
                      aria-label="Download screenshot"
                    >
                      <ArrowDownTrayIcon />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title="Close">
                  <span>
                    <button
                      ref={closePreviewBtnRef}
                      type="button"
                      className={styles.previewIconBtn}
                      onClick={() => setPreviewOpen(false)}
                      aria-label="Close preview"
                    >
                      <XMarkIcon />
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
            <div className={styles.previewModalBody}>
              {!imgLoaded && (
                <div className={styles.previewImgLoading}>
                  <CircularProgress size={28} color="primary" />
                </div>
              )}
              <img
                src={active.url}
                alt={active.description || pageUrl || 'Screenshot preview'}
                className={styles.previewModalImg}
                style={{ transform: `scale(${zoom})`, opacity: imgLoaded ? 1 : 0 }}
                onLoad={() => setImgLoaded(true)}
              />
            </div>
            <div className={styles.previewModalFooter}>
              <div className={styles.previewModalMeta}>
                <span>
                  Shot {Math.max(activeIdx, 0) + 1} of {Math.max(screenshots.length, 1)}
                  {live && followLive ? ' · Live updating' : ''}
                  {active.at ? ` · ${active.at}` : ''}
                </span>
                <span className={styles.previewModalHint}>← → navigate · Esc close · +/- zoom</span>
              </div>
              {screenshots.length > 1 && (
                <div className={styles.previewModalStrip}>
                  {screenshots.map((shot, index) => (
                    <button
                      key={`modal-${shot.url}-${index}`}
                      type="button"
                      className={`${styles.shotThumb} ${index === activeIdx ? styles.shotThumbActive : ''}`}
                      onClick={() => selectShot(index)}
                      title={shot.pageUrl || shot.description || `Screenshot ${index + 1}`}
                      aria-label={`Preview screenshot ${index + 1}${index === activeIdx ? ', selected' : ''}`}
                      aria-current={index === activeIdx ? 'true' : undefined}
                    >
                      <img src={shot.url} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
